import { canonicalUrl, safeJson } from "../../../shared/src/seo";
import type { StructuredData } from "../../src/lib/structured-data";
import { isPlatformShell, type PlatformShell } from "./shell";

/** Drate's own shell, as the root route loaded it, from inside a platform page's head. */
export function platformShellFrom(matches: readonly { loaderData?: unknown; routeId: string }[]) {
  const shell = matches.find((match) => match.routeId === "__root__")?.loaderData;
  return isPlatformShell(shell) ? shell : undefined;
}

export type PlatformPageHead = {
  pathname: string;
  title: string;
  description: string;
  canonicalPath?: string;
  noindex?: boolean;
  /**
   * A page with a picture of its own (a guide's cover) shares that instead of Drate's card: a
   * landscape one, since a shared link shows about 1.9:1 and crops anything taller to a strip.
   */
  image?: string;
  /** The shared picture's size and description, when the page knows them. */
  imageSize?: readonly [width: number, height: number];
  imageAlt?: string;
  article?: boolean;
  structuredData?: StructuredData | StructuredData[];
};

/**
 * A page of Drate's own site: its title, description, search rules and sharing tags (SiteLayout's
 * head without a creator). Search engines index Drate's production pages only.
 */
/** Drate's own card, shared by any page without a picture of its own. */
const drateCard = {
  path: "/og-3.png",
  size: [1200, 630],
  alt: "Drate: your work, your people. A website with memberships built in, shown on a phone.",
} as const;

export function platformPageHead(shell: PlatformShell | undefined, page: PlatformPageHead) {
  if (!shell) return {};
  const canonical = canonicalUrl(shell.origin, page.canonicalPath ?? page.pathname);
  const indexable = !page.noindex && shell.production;
  const image = new URL(page.image ?? drateCard.path, shell.origin).href;
  const imageSize = page.image ? page.imageSize : drateCard.size;
  const imageAlt = page.image ? page.imageAlt : drateCard.alt;
  const structured = page.noindex ? [] : [page.structuredData ?? []].flat();
  return {
    meta: [
      { title: page.title },
      { name: "description", content: page.description },
      { name: "robots", content: indexable ? "index,follow" : "noindex,nofollow" },
      { property: "og:type", content: page.article ? "article" : "website" },
      { property: "og:site_name", content: "Drate" },
      { property: "og:title", content: page.title },
      { property: "og:description", content: page.description },
      { property: "og:url", content: canonical },
      { property: "og:image", content: image },
      ...(imageSize
        ? [
            { property: "og:image:width", content: String(imageSize[0]) },
            { property: "og:image:height", content: String(imageSize[1]) },
          ]
        : []),
      ...(imageAlt ? [{ property: "og:image:alt", content: imageAlt }] : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: page.title },
      { name: "twitter:description", content: page.description },
      { name: "twitter:image", content: image },
      ...(imageAlt ? [{ name: "twitter:image:alt", content: imageAlt }] : []),
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: structured.map((item) => ({ type: "application/ld+json", children: safeJson(item) })),
  };
}
